import { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import './SplitButton.css';

export interface SplitButtonOption {
  label: string;
  onClick: () => void;
}

interface SplitButtonProps {
  /** All selectable options. The first one is active by default. */
  options: SplitButtonOption[];
  disabled?: boolean;
  /** CSS variant class suffix – defaults to 'danger' → btn-danger */
  variant?: string;
  size?: 'small' | 'medium' | 'large';
}

export function SplitButton({
  options,
  disabled = false,
  variant = 'danger',
  size = 'medium',
}: SplitButtonProps) {
  const [open, setOpen] = useState(false);
  // Which option is currently active (shown on the main button)
  const [activeIndex, setActiveIndex] = useState(0);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const wrapperRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const activeOption = options[activeIndex] ?? options[0];
  const otherOptions = options.map((opt, i) => ({ opt, i })).filter(({ i }) => i !== activeIndex);

  // Position dropdown so its right edge aligns with the right edge of the wrapper
  const updatePosition = () => {
    if (!wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    setDropdownStyle({
      position: 'fixed',
      top: rect.bottom,
      right: window.innerWidth - rect.right,
      minWidth: rect.width,
      zIndex: 9999,
    });
  };

  const handleToggle = () => {
    if (!open) updatePosition();
    setOpen((prev) => !prev);
  };

  // Close on outside click
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close + reposition on scroll/resize
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const close = () => setOpen(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  const baseClass = `btn btn-${variant} btn-${size}`;

  const dropdown =
    open && otherOptions.length > 0
      ? ReactDOM.createPortal(
          <div className="split-btn-dropdown" style={dropdownStyle} ref={dropdownRef}>
            {otherOptions.map(({ opt, i }) => (
              <button
                key={i}
                className="split-btn-dropdown-item"
                onClick={() => {
                  // Make the picked option the active one on the main button
                  setActiveIndex(i);
                  setOpen(false);
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>,
          document.body,
        )
      : null;

  return (
    <div className={`split-btn-wrapper${disabled ? ' split-btn-disabled' : ''}`} ref={wrapperRef}>
      <button
        className={`${baseClass} split-btn-main`}
        onClick={activeOption.onClick}
        disabled={disabled}
      >
        {activeOption.label}
      </button>

      <button
        ref={toggleRef}
        className={`${baseClass} split-btn-toggle`}
        onClick={handleToggle}
        disabled={disabled}
        aria-label="Show more actions"
      >
        <i className={`fa-solid fa-chevron-${open ? 'up' : 'down'}`} />
      </button>

      {dropdown}
    </div>
  );
}

export default SplitButton;
